/**
 * `<EventPhasePill />` — Story 5.4 (UX §4.1 line 885, AC line 1012).
 *
 * Colored chip showing the event lifecycle `phase` + optional `countdown`
 * text. Consumed by Story 7.1 admin dashboard, Story 7.5 moderation
 * queue, Story 12.7 reveal display, etc.
 *
 * Phase enumeration (Critical Scope Decision #4 — matches FSM verbatim
 * from architecture §3.2 + Story 2.7 `assertActionAllowed`):
 *   - `created`            — gray (`--text-muted`)
 *   - `submission-open`  — green (`--success`)
 *   - `voting-open`      — brand (`--brand-primary` + `--text-on-brand`)
 *   - `voting-closed`    — yellow (`--warning`)
 *   - `revealed`         — purple via `var(--phase-revealed)` (Story 33.3
 *                          promoted the literal to a token).
 *   - `archived`         — muted (`--text-muted`)
 *
 * Story 26.2: vestigial `'published'` case removed (FSM only produces 6
 * phases; UX skill review approved removal — see
 * `_bmad-output/implementation-artifacts/epic-26-story-2-ux-review.md`).
 *
 * Story 26.2: label set refreshed for scanability + verb-alignment with
 * the submitter UI. New labels: Setup / Submit Open / Voting Open /
 * Voting Closed / Winners Revealed / Archived.
 *
 * Countdown:
 *   - Component does NOT compute the countdown. Consumer formats the
 *     string (e.g., `"closes in 1h 23m"`) and passes via `countdown`.
 *
 * Sizing: `--radius-sm` chip; `--space-2` horizontal padding;
 * `--text-sm-size` font; min-height 28px (status indicator, NOT
 * a touch target — chip is non-interactive).
 */

export type EventPhase =
  | 'created'
  | 'submission-open'
  | 'voting-open'
  | 'voting-closed'
  | 'revealed'
  | 'archived';

export interface EventPhasePillProps {
  phase: EventPhase;
  /** Pre-formatted string from consumer (e.g., "closes in 1h 23m"). */
  countdown?: string;
}

interface PhaseStyle {
  background: string;
  color: string;
  label: string;
}

function phaseStyle(phase: EventPhase): PhaseStyle {
  switch (phase) {
    case 'created':
      return {
        background: 'var(--text-muted)',
        color: 'var(--text-on-brand)',
        label: 'Setup',
      };
    case 'submission-open':
      return {
        background: 'var(--success)',
        color: 'var(--text-on-brand)',
        label: 'Submit Open',
      };
    case 'voting-open':
      return {
        background: 'var(--brand-primary)',
        color: 'var(--text-on-brand)',
        label: 'Voting Open',
      };
    case 'voting-closed':
      return {
        background: 'var(--warning)',
        color: 'var(--text-on-brand)',
        label: 'Voting Closed',
      };
    case 'revealed':
      return {
        background: 'var(--phase-revealed)',
        color: '#FFFFFF',
        label: 'Winners Revealed',
      };
    case 'archived':
      return {
        background: 'var(--text-muted)',
        color: 'var(--text-on-brand)',
        label: 'Archived',
      };
  }
}

/**
 * `EventPhasePill` — colored status chip for the event lifecycle.
 */
export function EventPhasePill({ phase, countdown }: EventPhasePillProps) {
  const s = phaseStyle(phase);
  return (
    <span
      data-phase={phase}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        background: s.background,
        color: s.color,
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm-size)',
        fontWeight: 500,
        minHeight: 28,
        whiteSpace: 'nowrap',
      }}
    >
      <span data-testid="phase-label">{s.label}</span>
      {countdown && (
        <span
          data-testid="phase-countdown"
          style={{ opacity: 0.85, fontSize: 'var(--text-xs-size)' }}
        >
          {countdown}
        </span>
      )}
    </span>
  );
}
